/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Role } from "./types";

type Props = { roles: Role[]; onClose: (changed: boolean) => void };

const SUGGESTED = ["chef", "floor", "bar", "barista", "shift_lead", "host", "dishwasher"];

// The shop's competences: what a shift can require and a person can do.
export const RolesModal = defineComponent<Props>({
  name: "RolesModal",
  props: ["roles", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const roles = ref<Role[]>([...props.roles]);
    const draft = ref("");
    const changed = ref(false);

    const add = async (name: string) => {
      if (!name.trim()) return;
      try {
        const role = (await trpc.rota.roles.save.mutate({ name: name.trim() })) as Role;
        roles.value = [...roles.value, role];
        draft.value = "";
        changed.value = true;
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
      }
    };

    const rename = async (role: Role, name: string) => {
      if (!name.trim() || name === role.name) return;
      await trpc.rota.roles.save.mutate({ id: role.id, name: name.trim() });
      changed.value = true;
    };

    const remove = async (role: Role) => {
      if (!confirm(t("workday.team.role_delete_confirm").replace("{name}", role.name))) return;
      await trpc.rota.roles.delete.mutate({ id: role.id });
      roles.value = roles.value.filter((r) => r.id !== role.id);
      changed.value = true;
    };

    return () => {
      const have = new Set(roles.value.map((r) => r.name.toLowerCase()));
      const suggestions = SUGGESTED.map((k) => t(`workday.team.role_${k}`)).filter((n) => !have.has(n.toLowerCase()));
      return (
        <LemmonModal
          title={t("workday.team.roles_title")}
          onClose={() => props.onClose(changed.value)}
          renderButtons={() => (
            <div class="border-t p-4 text-right">
              <button class="btn btn-primary" onClick={() => props.onClose(changed.value)}>
                {t("workday.common.done")}
              </button>
            </div>
          )}
        >
          <div class="space-y-4 p-6">
            <p class="text-sm text-base-content/70">{t("workday.team.roles_help")}</p>
            <ul class="space-y-2">
              {roles.value.map((r) => (
                <li key={r.id} class="flex items-center gap-2">
                  <input
                    class="input input-bordered input-sm flex-1"
                    value={r.name}
                    onChange={(e: Event) => rename(r, (e.target as HTMLInputElement).value)}
                  />
                  <button class="btn btn-ghost btn-sm btn-square" aria-label={t("workday.common.delete")} onClick={() => remove(r)}>
                    <span class="material-symbols-outlined text-base">delete</span>
                  </button>
                </li>
              ))}
            </ul>
            <div class="join w-full">
              <input
                class="input join-item input-bordered input-sm w-full"
                placeholder={t("workday.team.role_placeholder")}
                v-model={draft.value}
                onKeydown={(e: KeyboardEvent) => e.key === "Enter" && add(draft.value)}
              />
              <button class="btn join-item btn-neutral btn-sm" disabled={!draft.value.trim()} onClick={() => add(draft.value)}>
                {t("workday.tasks.add")}
              </button>
            </div>
            {suggestions.length > 0 && (
              <div class="flex flex-wrap gap-1">
                {suggestions.map((s) => (
                  <button key={s} class="btn btn-ghost btn-xs border-base-300" onClick={() => add(s)}>
                    <span class="material-symbols-outlined text-sm">add</span>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </LemmonModal>
      );
    };
  },
});
